/* eslint-disable jsx-a11y/alt-text */
import React, { Component } from 'react'
import debounce from 'debounce'
import loadAssets from './Assets'
import './App.css'

const REF_WIDTH = 1980
const REF_HEIGHT = 1114
const DEFAULT_LANG = 'it'
const BUILDING_COORDS = [
  { name: 'hospital', coords: [725, 0, 890, 300] },
  { name: 'square', coords: [1325, 250, 1725, 550] },
  { name: 'palace', coords: [1400, 575, 1750, 1050] },
  { name: 'ambassy', coords: [900, 800, 1225, 1100] },
  { name: 'fabric', coords: [350, 625, 600, 950] },
  { name: 'hydric', coords: [200, 350, 500, 600] }
]

const Close = props => (
  <img src={props.src} alt="Close" className="Close" onClick={props.onClick} />
)

const Video = props => (
  <video className="BackgroundVideo" loop autoPlay src={props.src} />
)

const Preload = props => {
  return (
    <div className="Preload">
      {Object.keys(props.assets.images || {}).map(assetName => (
        <img
          src={props.assets.images[assetName]}
          alt="Loading..."
          key={assetName}
        />
      ))}
    </div>
  )
}

const Layer = props => (
  <div
    useMap={props.usemap}
    className={`Layer ${props.className || ''}`.trim()}
  >
    <img
      src={props.src}
      style={{ cursor: props.onClick ? 'pointer' : 'default' }}
      onClick={props.onClick ? () => props.onClick() : () => {}}
    />
  </div>
)

const BuildingsMap = props => {
  return (
    <div className="BuildingsMap">
      <Layer
        className="Base"
        src={props.assets.images.buildings}
        usemap="#buildingsMap"
      />
      <div name="buildingsMap">
        {props.buildingCoords.map(item => (
          <div
            className="BuildingsTargetArea"
            style={{
              left: parseInt(item.coords[0] * props.xRatio, 10) + 'px',
              top: parseInt(item.coords[1] * props.yRatio, 10) + 'px',
              right:
                parseInt((REF_WIDTH - item.coords[2]) * props.xRatio, 10) +
                'px',
              bottom:
                parseInt((REF_HEIGHT - item.coords[3]) * props.yRatio, 10) +
                'px'
            }}
            key={item.name}
            alt={item.name}
            onClick={() => props.onClick(item.name)}
          />
        ))}
      </div>
    </div>
  )
}

const Layers = props => {
  return (
    <section className="Layers">
      <BuildingsMap {...props} />
      {!props.building && !props.detail && (
        <Layer className="Pins" src={props.assets.images.pins} />
      )}
      {props.building && !props.detail && (
        <React.Fragment>
          <Layer
            className="Building"
            src={props.buildings[props.building].src}
          />
          <Layer
            className={`Info ${
              props.buildings[props.building].right ? 'right' : ''
            }`.trim()}
            src={props.buildings[props.building].content}
            onClick={() =>
              window.open(props.buildings[props.building].link, '_blank')
            }
          />
        </React.Fragment>
      )}
      {props.detail && (
        <React.Fragment>
          <Layer
            className="BuildingDetails"
            src={props.buildingLayers[props.detail].src}
          />
          <Layer
            className="Info"
            src={props.buildingLayers[props.detail].content}
            onClick={() =>
              window.open(props.buildingLayers[props.detail].link, '_blank')
            }
          />
        </React.Fragment>
      )}
    </section>
  )
}

const Nav = props => (
  <nav className="Nav">
    <span
      className={`Btn ${props.detail === 'iot' ? 'selected' : ''}`}
      onClick={() => props.onClick('iot')}
    >
      <img src={props.iot} alt="" />
    </span>
    <span
      className={`Btn ${props.detail === 'wireless' ? 'selected' : ''}`}
      onClick={() => props.onClick('wireless')}
    >
      <img src={props.wireless} alt="" />
    </span>
    <span
      className={`Btn ${props.detail === 'wired' ? 'selected' : ''}`}
      onClick={() => props.onClick('wired')}
    >
      <img src={props.wired} alt="" />
    </span>
  </nav>
)

class App extends Component {
  state = {
    lang: DEFAULT_LANG,
    assets: null,
    detail: null,
    building: null,
    buildings: null,
    buildingLayers: null,
    xRatio: 1,
    yRatio: 1
  }

  componentDidUpdate(prevProps, prevState) {
    const lang = prevProps.match.params.lang || DEFAULT_LANG
    if (prevState.lang !== lang) {
      this.updateLang()
    }
  }

  componentDidMount() {
    this.updateLang()
    this.updateRatio()
    window.onresize = debounce(this.updateRatio.bind(this), 200)
  }

  componentWillUnmount() {
    window.onresize.clear()
  }

  handleClose() {
    this.setState(() => ({ detail: null, building: null }))
  }

  updateLang() {
    const lang = this.props.match.params.lang || DEFAULT_LANG
    const assets = loadAssets(lang)
    this.setState(() => ({
      lang: lang,
      assets: assets,
      buildings: {
        hydric: {
          src: assets.images.building1,
          content: assets.images.k50,
          right: true,
          link: `https://www.bmeters.com/${lang}/bm_product/wde-k50/`
        },
        fabric: {
          src: assets.images.building2,
          content: assets.images.hydrosplit,
          right: true,
          link: `https://www.bmeters.com/${lang}/bm_product/hydrosplit-m3/`
        },
        ambassy: {
          src: assets.images.building3,
          content: assets.images.hydrocal,
          right: false,
          link: `https://www.bmeters.com/${lang}/bm_product/hydrocal-m3/`
        },
        palace: {
          src: assets.images.building4,
          content: assets.images.gsd8,
          right: false,
          link: `https://www.bmeters.com/${lang}/bm_product/gsd8-rfm/`
        },
        square: {
          src: assets.images.building5,
          content: assets.images.gmdm,
          right: false,
          link: `https://www.bmeters.com/${lang}/bm_product/gmdm-i/`
        },
        hospital: {
          src: assets.images.building6,
          content: assets.images.hydroclima,
          right: true,
          link: `https://www.bmeters.com/${lang}/bm_product/hydroclima/`
        }
      },
      buildingLayers: {
        iot: {
          src: assets.images.buildingsIOT,
          content: assets.images.detailIOT,
          link: `https://www.bmeters.com/${lang}/product_categories/iot/`
        },
        wireless: {
          src: assets.images.buildingsWIRELESS,
          content: assets.images.detailWIRELESS,
          link: `https://www.bmeters.com/${lang}/product_categories/m-bus-wireless/`
        },
        wired: {
          src: assets.images.buildingsWIRED,
          content: assets.images.detailWIRED,
          link: `https://www.bmeters.com/${lang}/product_categories/m-bus-wired/`
        }
      }
    }))
  }

  updateRatio() {
    const x = window.innerWidth / REF_WIDTH
    const y = window.innerHeight / REF_HEIGHT
    this.setState(() => ({
      xRatio: x,
      yRatio: y
    }))
  }

  setDetail(detail) {
    this.setState(() => ({ detail }))
  }

  setBuilding(building) {
    this.setState(() => ({ building }))
  }

  render() {
    return (
      this.state.assets && (
        <main>
          <Preload assets={this.state.assets} />
          <Video src={this.state.assets.others.video} />
          <Layers
            assets={this.state.assets}
            buildings={this.state.buildings}
            buildingLayers={this.state.buildingLayers}
            buildingCoords={BUILDING_COORDS}
            building={this.state.building}
            detail={this.state.detail}
            xRatio={this.state.xRatio}
            yRatio={this.state.yRatio}
            onClick={id => this.setBuilding(id)}
          />
          {!this.state.building && (
            <Nav
              detail={this.state.detail}
              iot={this.state.assets.images.buttonIOT}
              wireless={this.state.assets.images.buttonWIRELESS}
              wired={this.state.assets.images.buttonWIRED}
              onClick={v => this.setDetail(v)}
            />
          )}
          {(this.state.detail || this.state.building) && (
            <Close
              src={this.state.assets.images.close}
              onClick={() => this.handleClose()}
            />
          )}
        </main>
      )
    )
  }
}

export default App



// WEBPACK FOOTER //
// ./src/App.js